// Reusable client selector backed by the shared useClients() cache.
//
//   value     = clientId (number | string | null)
//   onChange  = (id | null) => void
//   onSelect  = (clientObj | null) => void   — optional; lets callers prefill
//               snapshot fields (e.g. the invoice form) from the chosen client.
//
// Themed for dark/light mode via the `colors` prop. A searchable Autocomplete
// so it scales past a handful of clients.
import { Autocomplete, TextField } from "@mui/material";
import { useClients } from "../hooks/queries";

// Consistent label: "CLIENTCODE (Client Name)". Falls back gracefully when one
// of the two is missing so the dropdown never shows a blank/odd value.
function clientLabel(o) {
  if (!o) return "";
  const code = (o.clientCode || "").trim();
  const name = (o.name || "").trim();
  if (code && name) return `${code} (${name})`;
  return code || name;
}

export default function ClientPicker({
  value,
  onChange,
  onSelect,
  colors,
  fieldSx,
  placeholder = "Select a client…",
}) {
  const { data } = useClients();
  const clients = data || [];
  const selected =
    clients.find((c) => String(c.id) === String(value ?? "")) || null;

  return (
    <Autocomplete
      options={clients}
      value={selected}
      getOptionLabel={clientLabel}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_e, val) => {
        onChange(val ? val.id : null);
        if (onSelect) onSelect(val || null);
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} sx={fieldSx} />
      )}
      slotProps={{
        paper: {
          sx: {
            background: colors.menuBg,
            border: `1px solid ${colors.cardBorder}`,
            color: colors.text,
            "& .MuiAutocomplete-option": { fontSize: 14 },
            "& .MuiAutocomplete-option:hover": { background: colors.hover },
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              background: "rgba(79, 70, 229, 0.12)",
            },
          },
        },
        clearIndicator: { sx: { color: colors.textMuted } },
        popupIndicator: { sx: { color: colors.textMuted } },
      }}
    />
  );
}
